import { call, take, fork, put } from 'redux-saga/effects'
import i18n from 'config/i18n'
import { handleRequest } from './requests'
import { STATE_KEYS } from '../state/requests'
import {
    RegisterTaskRequestPayload,
    TasksListRequestPayload,
    TaskState,
    TasksActions,
    saveRegisteredTask,
    saveTasksList,
    UpdateTaskRequestPayload,
    DeleteTaskRequestPayload,
    removeFromTasksList,
    UpdateTaskPayoutsRequestPayload,
    UpdateTaskScheduleRequestPayload,
    UpdateTasksOrderingRequestPayload,
} from '../state/tasks'
import { saveCampaignEditorErrors, resetCampaignEditorErrors } from 'state/error'
import {
    getTasksList,
    registerTask,
    updateTask,
    deleteTask,
    TasksListResponse,
    updateTaskPayouts,
    updateTaskSchedules,
    updateTasksOrdering,
} from '../services/api/tasks'
import { showSnackBar } from '../services/toast'
import { ERROR_CODES } from 'state/error'

export function* handleGetTasksList(payload: TasksListRequestPayload): any {
    yield handleRequest<TasksListRequestPayload, TasksListResponse>({
        stateKey: STATE_KEYS.TasksList,
        payload,
        ApiHandler: getTasksList,
        *onSuccess(response) {
            if (payload.verifyCaptionReviewTaskUuid) {
                const tasks = response.data?.data.results
                if (tasks) {
                    const targetTask = tasks.find(task => task.uuid == payload.verifyCaptionReviewTaskUuid)
                    const isCaptionReviewDone = targetTask?.steps[0].is_caption_reviews_done
                    yield call(() => {
                        if (payload.captionReviewCallback) {
                            if (typeof isCaptionReviewDone === 'boolean') {
                                payload.captionReviewCallback(isCaptionReviewDone)
                            }
                        }
                    })
                }
            } else {
                yield put(
                    saveTasksList({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                        isMockSubmissionProof: payload.isMockSubmissionProof,
                    }),
                )
            }
        },
    })
}

export function* watchTasksListRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.TASKS_LIST_REQUEST)
        yield fork(handleGetTasksList, action.payload)
    }
}

export function* handleRegisterTask(payload: RegisterTaskRequestPayload): any {
    yield handleRequest<RegisterTaskRequestPayload, TaskState[]>({
        stateKey: STATE_KEYS.RegisterTask,
        payload,
        ApiHandler: registerTask,
        *onSuccess(response) {
            yield put(
                saveRegisteredTask({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            yield put(
                saveTasksList({
                    data: response.data !== undefined ? response.data.data : undefined,
                    isMockSubmissionProof: true,
                }),
            )
        },
    })
}

export function* watchRegisterTaskRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.REGISTER_TASK_REQUEST)
        yield fork(handleRegisterTask, action.payload)
    }
}

export function* handleUpdateTask(payload: UpdateTaskRequestPayload): any {
    yield handleRequest<UpdateTaskRequestPayload, TaskState>({
        stateKey: STATE_KEYS.UpdateTask,
        payload,
        ApiHandler: updateTask,
        *onSuccess(response) {
            yield put(
                saveTasksList({
                    data: response.data !== undefined ? [response.data.data] : undefined,
                    isMockSubmissionProof: true,
                }),
            )
            yield put(resetCampaignEditorErrors())
            if (payload.onSuccessCallback) {
                payload.onSuccessCallback()
            } else {
                showSnackBar({
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title,
                type: 'error',
            })
        },
    })
}

export function* watchUpdateTaskRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.UPDATE_TASK_REQUEST)
        yield fork(handleUpdateTask, action.payload)
    }
}

export function* handleDeleteTask(payload: DeleteTaskRequestPayload): any {
    yield handleRequest<DeleteTaskRequestPayload, any>({
        stateKey: STATE_KEYS.DeleteTask,
        payload,
        ApiHandler: deleteTask,
        *onSuccess(response) {
            yield put(removeFromTasksList({ taskUuid: payload.taskUuid }))
        },
    })
}

export function* watchDeleteTaskRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.DELETE_TASK_REQUEST)
        yield fork(handleDeleteTask, action.payload)
    }
}

export function* handleUpdateTaskPayouts(payload: UpdateTaskPayoutsRequestPayload): any {
    yield handleRequest<UpdateTaskPayoutsRequestPayload, TaskState>({
        stateKey: STATE_KEYS.UpdateTask,
        payload,
        ApiHandler: updateTaskPayouts,
        *onSuccess(response) {
            yield put(
                saveTasksList({
                    data: response.data !== undefined ? [response.data.data] : undefined,
                    isMockSubmissionProof: true,
                }),
            )
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges'),
                type: 'positive',
            })
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.atLeastOneTaskDetailsEnteredSubtitle')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchUpdateTaskPayoutsRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.UPDATE_TASK_PAYOUTS_REQUEST)
        yield fork(handleUpdateTaskPayouts, action.payload)
    }
}

export function* handleUpdateTaskSchedules(payload: UpdateTaskScheduleRequestPayload): any {
    yield handleRequest<UpdateTaskScheduleRequestPayload, TaskState>({
        stateKey: STATE_KEYS.UpdateTask,
        payload,
        ApiHandler: updateTaskSchedules,
        *onSuccess(response) {
            yield put(
                saveTasksList({
                    data: response.data !== undefined ? [response.data.data] : undefined,
                    isMockSubmissionProof: true,
                }),
            )
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges'),
                type: 'positive',
            })
        },
        *onError(response) {
            const message = i18n.t('errorMessage.businessLogic.atLeastOneTaskDetailsEnteredSubtitle')
            if (
                response &&
                response['general_errors'] &&
                Array.isArray(response['general_errors']['reasons']) &&
                response['general_errors']['reasons'].length > 0
            ) {
                const errors = response['general_errors']['reasons'].map((reason: string) => {
                    switch (reason) {
                        case ERROR_CODES.PAST_SCHEDULE_UPDATE_NOT_ALLOWED:
                            return i18n.t('errorMessage.businessLogic.startDatePast')
                            break
                        case ERROR_CODES.SCHEDULE_MUST_LATER_THAN_NOW:
                            return i18n.t('errorMessage.businessLogic.pastDateEnteredSubtitle')
                            break
                        default:
                            return i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
                    }
                })
                errors.forEach((error: string) => {
                    return showSnackBar({
                        title: error,
                        type: 'error',
                    })
                })
            } else {
                yield call(showSnackBar, {
                    title: message,
                    type: 'error',
                })
            }
        },
    })
}

export function* watchUpdateTaskSchedulesRequestFlow(): any {
    while (true) {
        const action = yield take(TasksActions.UPDATE_TASK_SCHEDULES_REQUEST)
        yield fork(handleUpdateTaskSchedules, action.payload)
    }
}

export function* handleUpdateTasksOrdering(payload: UpdateTasksOrderingRequestPayload): any {
    yield handleRequest<UpdateTasksOrderingRequestPayload, any>({
        stateKey: STATE_KEYS.UpdateTasksOrdering,
        payload,
        ApiHandler: updateTasksOrdering,
    })
}

export function* watchUpdateTasksOrdering(): any {
    while (true) {
        const action = yield take(TasksActions.UPDATE_TASK_ORDER)
        yield fork(handleUpdateTasksOrdering, action.payload)
    }
}
